const rawRowForFinding = (finding = {}) => finding.rawRow || finding.raw_row || {};

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || '';

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const boolishValue = (value) => {
  if (typeof value === 'boolean') return value;
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
  if (['false', 'no', 'n', '0'].includes(normalized)) return false;
  return null;
};

const displayValue = (value) => {
  if (!hasValue(value)) return 'not available';
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  return String(value).trim();
};

const displayBooleanValue = (value) => {
  const boolish = boolishValue(value);
  if (boolish === true) return 'TRUE';
  if (boolish === false) return 'FALSE';
  return displayValue(value);
};

const billingFlagSummary = (finding = {}) => {
  const expected = boolishValue(finding.expectedValue);
  const actual = boolishValue(finding.actualValue);

  if (expected === true && actual === false) {
    return 'GM row should bill this service month, but Is Billing is FALSE.';
  }
  if (expected === false && actual === true) {
    return 'GM row should not bill this service month, but Is Billing is TRUE.';
  }
  if (expected !== null && actual !== null) {
    return `GM Is Billing should be ${displayBooleanValue(expected)}, but the file has ${displayBooleanValue(actual)}.`;
  }
  if (actual !== null) {
    return `GM Is Billing is ${displayBooleanValue(actual)}, but the expected billing state could not be determined.`;
  }
  return 'GM Is Billing does not match the expected billing state for this service month.';
};

const expectedFoundSummary = (finding = {}, label = 'Value') => {
  const hasExpected = hasValue(finding.expectedValue);
  const hasActual = hasValue(finding.actualValue);
  if (hasExpected && hasActual) {
    return `${label} should be ${displayValue(finding.expectedValue)}; found ${displayValue(finding.actualValue)}.`;
  }
  if (hasExpected) {
    return `${label} should be ${displayValue(finding.expectedValue)}; found value was not available.`;
  }
  if (hasActual) {
    return `Found ${displayValue(finding.actualValue)}; expected ${label.toLowerCase()} was not available.`;
  }
  return `${label} needs review.`;
};

export const fileHealthSeverity = (finding = {}) => (
  finding.tier === 'error' ? 'error' : finding.tier === 'warning' ? 'warning' : 'info'
);

const titleCaseCode = (code = '') => String(code || 'File health finding')
  .toLowerCase()
  .split('_')
  .filter(Boolean)
  .map((part) => {
    if (part === 'cpq') return 'CPQ';
    if (part === 'gm') return 'GM';
    if (part === 'bac') return 'BAC';
    return `${part.charAt(0).toUpperCase()}${part.slice(1)}`;
  })
  .join(' ');

export const fileHealthCodeLabel = (code = '') => {
  if (code === 'BACK_BILLED') return 'Back billed CPQ row';
  if (code === 'OUT_OF_CYCLE_CPQ_ROW') return 'Back billed CPQ row';
  if (code === 'BACK_CREDIT') return 'Back credit';
  if (code === 'MISSING_SUBSEQUENT_ROUND_CREDIT') return 'Missing credit on subsequent round';
  if (code === 'IS_BILLING_FLAG_WRONG') return 'Billing flag mismatch';
  if (code === 'BILLING_END_WRONG') return 'Billing end mismatch';
  if (code === 'BILLING_START_WRONG') return 'Billing start mismatch';
  if (code === 'LEGACY_CPQ_CODE_MAPPED') return 'Legacy CPQ code mapped';
  if (code === 'CPQ_DUPLICATE_PRODUCT') return 'Duplicate CPQ product';
  return titleCaseCode(code);
};

export const fileHealthNotes = (finding = {}) => {
  const raw = rawRowForFinding(finding);
  return firstValue(
    raw['Billing Reconciliation Notes'],
    raw['GM NOTES'],
    raw.Notes,
    raw.notes,
    raw.Note,
  );
};

export const fileHealthDealerName = (finding = {}) => {
  const raw = rawRowForFinding(finding);
  return firstValue(
    finding.dealerName,
    finding.dealer_name,
    raw['Dealer Name'],
    raw.dealerName,
  );
};

export const fileHealthBrand = (finding = {}) => {
  const raw = rawRowForFinding(finding);
  return firstValue(
    finding.brand,
    finding.productBrand,
    finding.product_brand,
    raw['Product Brand'],
    raw.Brand,
    raw.brand,
  );
};

export const fileHealthSummaryText = (finding = {}) => {
  const notes = fileHealthNotes(finding);
  if (notes) return notes;
  switch (finding.code) {
    case 'BACK_BILLED':
    case 'OUT_OF_CYCLE_CPQ_ROW':
      return `CPQ is trying to bill ${hasValue(finding.actualValue) ? displayValue(finding.actualValue) : 'a prior service month'} outside this reconciliation month.`;
    case 'BACK_CREDIT':
      return hasValue(finding.actualValue)
        ? `CPQ credit ${displayValue(finding.actualValue)} is outside this reconciliation month.`
        : 'CPQ credit is outside this reconciliation month.';
    case 'MISSING_SUBSEQUENT_ROUND_CREDIT':
      return hasValue(finding.expectedValue)
        ? `Prior-round credit ${displayValue(finding.expectedValue)} is missing from the current CPQ credit file.`
        : 'A prior-round credit is missing from the current CPQ credit file.';
    case 'IS_BILLING_FLAG_WRONG':
      return billingFlagSummary(finding);
    case 'BILLING_START_WRONG':
      return expectedFoundSummary(finding, 'Billing Start Date');
    case 'BILLING_END_WRONG':
      return expectedFoundSummary(finding, 'Billing End Date');
    case 'END_BEFORE_START':
      return hasValue(finding.actualValue)
        ? `Billing Start Date must be on or before Billing End Date; found ${displayValue(finding.actualValue)}.`
        : 'Billing Start Date must be on or before Billing End Date.';
    case 'LEGACY_CPQ_CODE_MAPPED':
      return hasValue(finding.actualValue)
        ? `Legacy CPQ product code was mapped to ${displayValue(finding.actualValue)}.`
        : 'Legacy CPQ product code was mapped to the current billing product code.';
    case 'CPQ_DUPLICATE_PRODUCT':
      return 'CPQ contains duplicate billable product rows for this BAC/product.';
    default:
      return expectedFoundSummary(finding, fileHealthCodeLabel(finding.code));
  }
};

export const fileHealthContext = (finding = {}) => ({
  source: 'file_health',
  title: `${fileHealthCodeLabel(finding.code)} - BAC ${finding.bac}`,
  code: finding.code,
  severity: fileHealthSeverity(finding),
  dealerName: fileHealthDealerName(finding),
  brand: fileHealthBrand(finding),
  summary: fileHealthSummaryText(finding),
  detail: `${String(finding.fileKind || 'file').toUpperCase()} row ${finding.rowNumber || '-'}`,
  findingId: finding.id,
  findingFingerprint: finding.findingFingerprint,
  broadAlertKey: finding.broadAlertKey,
  materialFactsHash: finding.materialFactsHash,
});

export const fileHealthOpenState = (finding = {}) => (
  !['addressed', 'resolved_by_action'].includes(finding.reviewState)
);
